import React, { useState } from 'react';
import { teleMaintenance, teleMaintenance_Templates, previewTemplate } from '../utils/teleMaintenance';

const TeleMaintenanceButton = ({ client }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [requestType, setRequestType] = useState('generic');
  const [selectedAccountId, setSelectedAccountId] = useState(''); // MODIFIÉ : utiliser l'ID du compte
  const [reason, setReason] = useState('');
  const [version, setVersion] = useState('');
  const [customText, setCustomText] = useState('');
  const [lastResult, setLastResult] = useState(null);
  
  // NOUVEAU : Récupérer le compte sélectionné
  const getSelectedAccount = () => {
    if (!client?.telemaintenance?.accounts?.length) return null;
    if (!selectedAccountId) {
      // Retourner le compte par défaut
      return client.telemaintenance.accounts.find(acc => acc.isDefault) || client.telemaintenance.accounts[0];
    }
    return client.telemaintenance.accounts.find(acc => acc.id === selectedAccountId);
  };
  
  // NOUVEAU : Initialiser le compte sélectionné lors de l'ouverture du modal
  const handleModalOpen = () => {
    setIsModalOpen(true);
    // Présélectionner le compte par défaut
    if (client?.telemaintenance?.accounts?.length > 0) {
      const defaultAccount = client.telemaintenance.accounts.find(acc => acc.isDefault);
      if (defaultAccount) {
        setSelectedAccountId(defaultAccount.id);
      }
    }
  };

  // Vérifier si la télémaintenance est configurée
  if (!client?.remoteMaintenance || !client?.telemaintenance?.enabled || !client?.telemaintenance?.recipientEmail?.trim()) {
    return null;
  }

  const handleSendRequest = async () => {
    setIsLoading(true);
    setLastResult(null);

    try {
      const selectedAccount = getSelectedAccount();
      const requestData = {
        type: requestType,
        accountName: selectedAccount?.account || client.telemaintenance.defaultAccount || '[COMPTE NON DÉFINI]',
        reason: reason,
        version: version,
        customText: requestType === 'custom' ? customText : null
      };

      const result = await teleMaintenance.sendRequest(client.id, requestData);
      setLastResult(result);
      
      if (result.success) {
        // Réinitialiser le formulaire après succès
        setTimeout(() => {
          setIsModalOpen(false);
          setRequestType('generic');
          setSelectedAccountId('');
          setReason('');
          setVersion('');
          setCustomText('');
          setLastResult(null);
        }, 3000);
      }
    } catch (error) {
      setLastResult({
        success: false,
        message: error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrentTemplate = () => {
    if (requestType === 'custom') {
      return customText;
    }
    return client.telemaintenance.templateType === requestType 
      ? client.telemaintenance.defaultTemplate 
      : teleMaintenance_Templates[requestType]?.content || '';
  };

  const getPreviewContent = () => {
    const selectedAccount = getSelectedAccount();
    return previewTemplate(getCurrentTemplate(), {
      clientName: client.name,
      accountName: selectedAccount?.account || client.telemaintenance.defaultAccount || '[COMPTE]',
      reason: reason || '[RAISON]',
      version: version || '[VERSION]'
    });
  };

  return (
    <>
      <button
        onClick={handleModalOpen}
        className="inline-flex items-center px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-md transition-colors"
        title="Demander l'ouverture de la télémaintenance"
      >
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 7.89a1 1 0 001.42 0L21 7M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        Télémaintenance
      </button>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  📧 Demande de télémaintenance - {client.name}
                </h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={isLoading}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Informations de configuration */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
                <h3 className="font-medium text-blue-800 mb-2">📋 Configuration actuelle:</h3>
                <div className="text-sm text-blue-700">
                  <div><strong>Destinataire:</strong> {client.telemaintenance.recipientEmail}</div>
                  {client.telemaintenance.ccEmails?.length > 0 && (
                    <div><strong>En copie:</strong> {client.telemaintenance.ccEmails.join(', ')}</div>
                  )}
                  <div><strong>Comptes configurés:</strong> {client.telemaintenance.accounts?.length || 0} compte(s)</div>
                  {client.telemaintenance.accounts?.length > 0 && (
                    <div><strong>Compte par défaut:</strong> {client.telemaintenance.accounts.find(acc => acc.isDefault)?.name || 'Aucun'}</div>
                  )}
                  <div><strong>Template par défaut:</strong> {teleMaintenance_Templates[client.telemaintenance.templateType]?.name || 'Générique'}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Formulaire */}
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900">Paramètres de la demande</h3>
                  
                  {/* Type de demande */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Type de demande</label>
                    <div className="grid grid-cols-1 gap-2">
                      {Object.entries(teleMaintenance_Templates).map(([key, template]) => (
                        <label key={key} className="flex items-center">
                          <input
                            type="radio"
                            name="requestType"
                            value={key}
                            checked={requestType === key}
                            onChange={(e) => setRequestType(e.target.value)}
                            className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500"
                            disabled={isLoading}
                          />
                          <span className="text-sm">{template.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Sélection du compte */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Compte de télémaintenance</label>
                    {client.telemaintenance.accounts?.length > 0 ? (
                      <div className="space-y-2">
                        {client.telemaintenance.accounts.map((account) => (
                          <label key={account.id} className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                            <input
                              type="radio"
                              name="selectedAccount"
                              value={account.id}
                              checked={selectedAccountId === account.id || (!selectedAccountId && account.isDefault)}
                              onChange={(e) => setSelectedAccountId(e.target.value)}
                              className="mt-1 mr-3 h-4 w-4 text-orange-600 focus:ring-orange-500"
                              disabled={isLoading}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900">{account.name}</span>
                                {account.isDefault && (
                                  <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                    Par défaut
                                  </span>
                                )}
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  account.type === 'generic' ? 'bg-gray-100 text-gray-700' :
                                  account.type === 'nominative' ? 'bg-green-100 text-green-700' :
                                  'bg-purple-100 text-purple-700'
                                }`}>
                                  {account.type === 'generic' ? 'Générique' :
                                   account.type === 'nominative' ? 'Nominatif' : 'Externe'}
                                </span>
                              </div>
                              <div className="text-sm text-gray-600 mt-1">
                                Compte: <span className="font-mono bg-gray-100 px-1 rounded">{account.account}</span>
                                {account.description && (
                                  <span className="ml-2">• {account.description}</span>
                                )}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center text-gray-500">
                        <svg className="w-8 h-8 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                        </svg>
                        <p className="text-sm">Aucun compte configuré</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Configurez des comptes dans les paramètres de télémaintenance du client
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Champs conditionnels */}
                  {requestType === 'problem' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Raison de l'intervention</label>
                      <input
                        type="text"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="un problème de connexion patient"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={isLoading}
                      />
                    </div>
                  )}

                  {requestType === 'update' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Version à déployer</label>
                      <input
                        type="text"
                        value={version}
                        onChange={(e) => setVersion(e.target.value)}
                        placeholder="7.41.13"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={isLoading}
                      />
                    </div>
                  )}

                  {requestType === 'custom' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Message personnalisé</label>
                      <textarea
                        value={customText}
                        onChange={(e) => setCustomText(e.target.value)}
                        placeholder="Votre message personnalisé..."
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={isLoading}
                      />
                    </div>
                  )}
                </div>

                {/* Prévisualisation */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-4">Aperçu du message</h3>
                  <div className="bg-gray-50 border rounded-md p-4 h-64 overflow-y-auto">
                    <div className="text-sm font-medium text-gray-700 mb-2">Email qui sera envoyé:</div>
                    <div className="bg-white p-3 rounded border text-sm whitespace-pre-line">
                      {getPreviewContent()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Résultat */}
              {lastResult && (
                <div className={`mt-6 p-4 rounded-md ${
                  lastResult.success 
                    ? 'bg-green-50 border border-green-200 text-green-800' 
                    : 'bg-red-50 border border-red-200 text-red-800'
                }`}>
                  <div className="flex items-center">
                    {lastResult.success ? (
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                    <span className="font-medium">
                      {lastResult.success ? 'Demande envoyée avec succès!' : 'Erreur lors de l\'envoi'}
                    </span>
                  </div>
                  <div className="text-sm mt-1">{lastResult.message}</div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  disabled={isLoading}
                >
                  Annuler
                </button>
                <button
                  onClick={handleSendRequest}
                  disabled={isLoading || (requestType === 'custom' && !customText.trim()) || (requestType === 'problem' && !reason.trim()) || (requestType === 'update' && !version.trim())}
                  className="px-6 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white rounded-md transition-colors flex items-center"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      Envoyer la demande
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TeleMaintenanceButton;